/** Etiquetas en español y clases de badge para estados QMS (NC / reclamos / planes). */

const RECORD_STATUS: Record<string, string> = {
  submitted: 'Enviada',
  under_review: 'En revisión',
  dismissed: 'Desestimada',
  contingency_defined: 'Contingencia definida',
  contingency_executed: 'Contingencia ejecutada',
  contingency_verified: 'Contingencia verificada',
  corrective_decision_pending: 'Decisión AC pendiente',
  corrective_in_progress: 'AC en curso',
  closed_simple: 'Cerrada (simple)',
  closed_corrective: 'Cerrada (con AC)',
};

const REVIEW: Record<string, string> = {
  pending: 'Pendiente',
  pertinent: 'Pertinente',
  dismissed: 'Desestimada',
};

const ACTION_STATUS: Record<string, string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  done: 'Hecha',
  cancelled: 'Cancelada',
};

const ORIGIN: Record<string, string> = {
  internal: 'Interna',
  external: 'Externa',
};

const ATTR: Record<string, string> = {
  company: 'Empresa',
  supplier: 'Proveedor',
  client: 'Cliente',
  external_carrier: 'Transportista',
};

const TYP_MODE: Record<string, string> = {
  by_area: 'Área',
  by_process: 'Proceso',
};

function badgeNeutral() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground';
}

function badgeBlue() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200';
}

function badgeGreen() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200';
}

function badgeAmber() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100';
}

function badgeRed() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';
}

function badgePurple() {
  return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200';
}

export function labelQmsRecordStatus(status: string): string {
  return RECORD_STATUS[status] ?? status;
}

export function qmsRecordStatusBadgeClass(status: string): string {
  switch (status) {
    case 'submitted':
      return badgeNeutral();
    case 'under_review':
    case 'corrective_decision_pending':
      return badgeBlue();
    case 'dismissed':
      return badgeRed();
    case 'contingency_defined':
    case 'contingency_executed':
      return badgeAmber();
    case 'contingency_verified':
      return badgePurple();
    case 'corrective_in_progress':
      return badgeBlue();
    case 'closed_simple':
    case 'closed_corrective':
      return badgeGreen();
    default:
      return badgeNeutral();
  }
}

export function labelQmsReview(outcome: string): string {
  return REVIEW[outcome] ?? outcome;
}

export function qmsReviewBadgeClass(outcome: string): string {
  if (outcome === 'pertinent') return badgeGreen();
  if (outcome === 'dismissed') return badgeRed();
  return badgeNeutral();
}

export function labelQmsActionStatus(status: string): string {
  return ACTION_STATUS[status] ?? status;
}

export function qmsActionStatusBadgeClass(status: string): string {
  switch (status) {
    case 'done':
      return badgeGreen();
    case 'in_progress':
      return badgeBlue();
    case 'cancelled':
      return badgeRed();
    default:
      return badgeNeutral();
  }
}

export function labelQmsOrigin(origin: string): string {
  return ORIGIN[origin] ?? origin;
}

export function labelQmsAttributability(v: string): string {
  return ATTR[v] ?? v;
}

export function labelTypificationMode(m: string): string {
  return TYP_MODE[m] ?? m;
}

export function labelPlanType(t: 'contingency' | 'corrective'): string {
  return t === 'contingency' ? 'Contingencia' : 'Acción correctiva';
}

export function isCorrectiveDecisionActive(status: string): boolean {
  return status === 'corrective_decision_pending' || status === 'contingency_verified';
}

export type CorrectiveDecisionSummary = {
  decision: string;
  decidedAt: string | null;
  decidedBy: string | null;
  justification: string | null;
};

export function resolveCorrectiveDecisionSummary(opening: {
  status: string;
  originType?: string;
  correctiveWaived?: boolean;
  correctiveWaivedObservation?: string | null;
  internalTriggersCorrective?: boolean;
  closedSimpleAt?: string | null;
  updatedAt?: string;
  correctiveDecisionDecidedByEmail?: string | null;
  dynamicData?: unknown;
}): CorrectiveDecisionSummary | null {
  const takenStatuses = ['corrective_in_progress', 'closed_simple', 'closed_corrective'];
  const dyn = opening.dynamicData as Record<string, unknown> | undefined;
  const audit = dyn?._correctiveDecision as
    | { at?: string; waiveObservation?: string; internalTriggersCorrective?: boolean }
    | undefined;
  // La decisión puede haberse registrado dejando el caso abierto a la espera del cierre manual.
  const decisionTaken = takenStatuses.includes(opening.status) || !!audit || !!opening.correctiveWaived;
  if (!decisionTaken) {
    return null;
  }

  let decision: string;
  if (opening.status === 'corrective_in_progress' || opening.status === 'closed_corrective') {
    if (opening.internalTriggersCorrective || audit?.internalTriggersCorrective) {
      decision = 'Iniciar acción correctiva (desviación grave o recurrente)';
    } else if (opening.originType === 'external') {
      decision = 'Confirmar seguimiento de acción correctiva';
    } else {
      decision = 'Iniciar acción correctiva';
    }
  } else if (opening.correctiveWaived) {
    decision = 'No aplicar acción correctiva (origen externo, justificado)';
  } else {
    decision = 'Cerrar sin acción correctiva';
  }

  const decidedAt =
    audit?.at ??
    (opening.closedSimpleAt ? String(opening.closedSimpleAt) : null) ??
    (opening.status === 'corrective_in_progress' ? opening.updatedAt ?? null : null);

  return {
    decision,
    decidedAt,
    decidedBy: opening.correctiveDecisionDecidedByEmail ?? null,
    justification: opening.correctiveWaivedObservation ?? audit?.waiveObservation ?? null,
  };
}

export type QmsExpedienteAxis = { label: string; className: string };
export type QmsCorrectiveAxis = { label: string; className: string };

export function deriveExpedienteAxis(record: {
  status: string;
  reviewOutcome?: string;
}): QmsExpedienteAxis {
  if (record.reviewOutcome === 'dismissed' || record.status === 'dismissed') {
    return { label: 'Desestimado', className: badgeRed() };
  }
  if (record.status === 'closed_simple') {
    return { label: 'Cerrado (simple)', className: badgeGreen() };
  }
  if (record.status === 'closed_corrective') {
    return { label: 'Cerrado (con AC)', className: badgeGreen() };
  }
  return { label: 'Abierto', className: badgeAmber() };
}

export function deriveCorrectiveAxis(record: {
  status: string;
  correctiveWaived?: boolean;
  internalTriggersCorrective?: boolean;
}): QmsCorrectiveAxis {
  if (record.correctiveWaived || record.status === 'closed_simple') {
    return { label: 'No aplica', className: badgeNeutral() };
  }
  if (['corrective_in_progress', 'closed_corrective'].includes(record.status)) {
    return { label: 'Aplica', className: badgeBlue() };
  }
  if (['corrective_decision_pending', 'contingency_verified'].includes(record.status)) {
    return { label: 'Decisión pendiente', className: badgeAmber() };
  }
  if (record.internalTriggersCorrective) {
    return { label: 'Aplica', className: badgeBlue() };
  }
  return { label: '—', className: badgeNeutral() };
}

export function isCorrectiveActionClosedUi(ac: {
  status: string;
  efficacyVerifiedAt?: string | null;
  closedWithoutEfficacy?: boolean;
}): boolean {
  if (ac.status === 'cancelled') return true;
  if (ac.closedWithoutEfficacy) return true;
  return ac.status === 'done' && !!ac.efficacyVerifiedAt;
}

export function labelCorrectiveActionLifecycle(ac: {
  status: string;
  implementedAt?: string | null;
  efficacyVerifiedAt?: string | null;
  closedWithoutEfficacy?: boolean;
  efficacyResult?: string | null;
}): string {
  if (ac.status === 'cancelled') return 'Cancelada';
  if (ac.closedWithoutEfficacy) return 'Cerrada sin verificar eficacia';
  if (ac.efficacyVerifiedAt) return 'Eficacia verificada';
  if (ac.efficacyResult === 'failed') return 'Eficacia fallida — reanalizar causa raíz';
  if (ac.implementedAt) return 'Implementada';
  if (ac.status === 'in_progress') return 'En curso';
  return 'Pendiente';
}

export function labelEfficacyResult(result: string): string {
  const labels: Record<string, string> = {
    eliminated: 'Eliminó la causa raíz',
    minimized: 'Minimizó la causa raíz',
    failed: 'No eliminó ni minimizó la causa raíz',
  };
  return labels[result] ?? result;
}

export function labelAnalysisCycleReason(reason: string): string {
  return reason === 'efficacy_failed' ? 'Re-análisis (eficacia fallida)' : 'Análisis inicial';
}

export type QmsOpeningDecisionInput = Parameters<typeof resolveCorrectiveDecisionSummary>[0];

export type QmsRootCauseCycleRef = {
  cycleNumber: number;
  id?: string;
  steps?: unknown[];
  rootCauses?: unknown[];
};

export function getActiveRootCauseCycle<T extends QmsRootCauseCycleRef>(cycles: T[]): T | null {
  if (!cycles?.length) return null;
  return [...cycles].sort((a, b) => b.cycleNumber - a.cycleNumber)[0];
}

export function isRootCauseAnalysisCompleteUi(
  cycle: { steps?: unknown[]; rootCauses?: unknown[] } | null | undefined,
  participantCount: number
): boolean {
  if (!cycle) return false;
  return (cycle.steps?.length ?? 0) > 0 && (cycle.rootCauses?.length ?? 0) > 0 && participantCount > 0;
}

const RECORD_SUBTYPE: Record<string, string> = {
  opening: 'Apertura',
  treatment: 'Tratamiento',
  corrective_action: 'Acción correctiva (registro)',
};

export function labelQmsRecordSubtype(subtype: string): string {
  return RECORD_SUBTYPE[subtype] ?? subtype;
}

const CONTINGENCY_SUMMARY: Record<string, string> = {
  none: 'Sin contingencia',
  open: 'Contingencia abierta',
  executed: 'Contingencia ejecutada',
  verified: 'Contingencia verificada',
};

const CORRECTIVE_DECISION_SUMMARY: Record<string, string> = {
  pending: 'Decisión pendiente',
  applies: 'Aplica acción correctiva',
  waived: 'No aplica (justificada)',
  not_required: 'No requiere acción correctiva',
};

const CORRECTIVE_ACTION_SUMMARY: Record<string, string> = {
  none: 'Sin acción correctiva',
  in_progress: 'Acción correctiva en curso',
  implemented: 'Acción correctiva implementada',
  verified: 'Eficacia verificada',
  closed: 'Acción correctiva cerrada',
};

export type QmsStatusSummaryShape = {
  contingency: string;
  correctiveDecision: string;
  correctiveAction: string;
  record: string;
};

export function labelContingencySummary(v: string): string {
  return CONTINGENCY_SUMMARY[v] ?? v;
}

export function labelCorrectiveDecisionSummary(v: string): string {
  return CORRECTIVE_DECISION_SUMMARY[v] ?? v;
}

export function labelCorrectiveActionSummary(v: string): string {
  return CORRECTIVE_ACTION_SUMMARY[v] ?? v;
}

/**
 * El reclamo es una no conformidad detectada por el cliente: ambos kinds
 * (`non_conformity` y `claim`) se cierran como no conformidad.
 */
export function labelRecordClosureSummary(v: string): string {
  return v === 'closed' ? 'No conformidad cerrada' : 'No conformidad abierta';
}

export function labelCloseRecordButton(): string {
  return 'Cerrar no conformidad';
}

export function contingencySummaryBadgeClass(v: string): string {
  if (v === 'verified') return badgeGreen();
  if (v === 'executed') return badgeBlue();
  if (v === 'open') return badgeAmber();
  return badgeNeutral();
}

export function correctiveDecisionSummaryBadgeClass(v: string): string {
  if (v === 'applies') return badgeBlue();
  if (v === 'waived' || v === 'not_required') return badgeNeutral();
  return badgeAmber();
}

export function correctiveActionSummaryBadgeClass(v: string): string {
  if (v === 'closed' || v === 'verified') return badgeGreen();
  if (v === 'implemented' || v === 'in_progress') return badgeBlue();
  return badgeNeutral();
}

export function recordClosureSummaryBadgeClass(v: string): string {
  return v === 'closed' ? badgeGreen() : badgeAmber();
}

/** Espejo de `canManuallyCloseRecord` (backend) para habilitar el botón de cierre manual. */
export function canCloseRecordManually(s: QmsStatusSummaryShape): boolean {
  if (s.record !== 'open') return false;
  if (s.correctiveDecision === 'applies') return s.correctiveAction === 'closed';
  if (s.correctiveDecision === 'waived' || s.correctiveDecision === 'not_required') {
    return s.contingency === 'verified';
  }
  return false;
}
